import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { Job_Entity } from "../../entities/job";
import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";

const jobsAdapter = createEntityAdapter<Job_Entity>();
const initialState = jobsAdapter.getInitialState();

export const jobEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getJobs: build.query<
      EntityState<Job_Entity>,
      Record<string, unknown> | void
    >({
      query: (args) => {
        return {
          url: `jobs`,
          params: args,
        };
      },
      transformResponse: (responseData: Job_Entity[]) => {
        return jobsAdapter.setAll(initialState, responseData);
      },
      providesTags: ["jobs"],
    }),
    getJob: build.query<Job_Entity, number>({
      query: (id) => `jobs/${id}`,
      providesTags: (result, error, id) => [{ type: "jobs", id }],
    }),
    createJob: build.mutation<Job_Entity, Partial<Job_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `jobs`,
      }),
      invalidatesTags: [
        "jobs",
        "budgets",
        "projects",
        "projectTasks",
        "vtransactions",
      ],
    }),
    updateJob: build.mutation<void, { id: number; body: Partial<Job_Entity> }>({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `jobs/${args.id}`,
      }),
      // Deliberately no invalidatesTags — refreshing per cell rebuilt the grid
      // mid-typing and dropped input. See scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, [
          "jobs",
          "budgets",
          "projects",
          "projectTasks",
          "vtransactions",
        ]);
      },
    }),
    deleteJob: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `jobs/${id}`,
      }),
      invalidatesTags: [
        "jobs",
        "budgets",
        "projects",
        "projectTasks",
        "vtransactions",
      ],
    }),
  }),
});

export const {
  useGetJobQuery,
  useGetJobsQuery,
  useCreateJobMutation,
  useDeleteJobMutation,
  useUpdateJobMutation,
} = jobEndpoints;

export default jobEndpoints;
